import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FiShoppingCart, FiSearch } from 'react-icons/fi';
import { motion } from 'framer-motion';
import './Sidebar.css';
import AdminLogin from '../Admin/AdminLogin'; // Keep this if needed or remove if unused, but we'll remove usage below.
import useNotification from '../../hooks/useNotification';
import { useSiteData } from '../../context/DataContext';

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { settings } = useSiteData();
    const [cartCount, setCartCount] = useState(0);
    const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [animKey, setAnimKey] = useState(0); // Trigger to restart animation
    const { showSuccess } = useNotification();

    const logoLetters = [
        { char: "W", x: -100, y: 0 },   // From Left
        { char: "a", x: 0, y: -100 },   // From Top
        { char: "l", x: 0, y: 100 },    // From Bottom
        { char: "l", x: 100, y: 0 },    // From Right
        { char: "Z", x: 0, y: 100 }     // From Bottom (mix it up)
    ];

    const containerVariants = {
        hidden: { opacity: 1 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.5
            }
        }
    };

    const letterVariants = {
        hidden: (custom) => ({
            opacity: 0,
            x: custom.x,
            y: custom.y,
            scale: 0.5,
            filter: "blur(10px)"
        }),
        visible: {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            transition: {
                duration: 1.2, /* Slightly faster for 'lighter' feel */
                ease: "easeOut"
            }
        }
    };

    useEffect(() => {
        const updateCartCount = () => {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
            setCartCount(totalItems);
        };

        const checkAdminStatus = () => {
            const adminLoggedIn = localStorage.getItem('adminLoggedIn');
            setIsAdminLoggedIn(adminLoggedIn === 'true');
        };

        updateCartCount();
        checkAdminStatus();

        window.addEventListener('storage', () => {
            updateCartCount();
            checkAdminStatus();
        });

        window.addEventListener('cartUpdated', updateCartCount);

        const handleAdminLogout = () => setIsAdminLoggedIn(false);
        window.addEventListener('adminLogout', handleAdminLogout);

        return () => {
            window.removeEventListener('storage', updateCartCount);
            window.removeEventListener('cartUpdated', updateCartCount);
            window.removeEventListener('adminLogout', handleAdminLogout);
        };
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('adminLoggedIn');
        window.dispatchEvent(new CustomEvent('adminLogout'));
        navigate('/');
    };

    const handleUserClick = () => {
        const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
        if (isLoggedIn) {
            navigate('/admin/orders');
        } else {
            setShowLoginModal(true);
        }
    };

    const handleHomeClick = (e) => {
        if (location.pathname === '/') {
            e.preventDefault();
            setAnimKey(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setAnimKey(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    };

    return (
        <>
            <aside className="sidebar">
                <div className="sidebar-logo">
                    <NavLink to="/" onClick={handleHomeClick} style={{ textDecoration: 'none' }}>
                        <motion.div
                            key={animKey}
                            className="animated-logo-text"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {logoLetters.map((letter, index) => (
                                <motion.span
                                    key={index}
                                    custom={letter}
                                    variants={letterVariants}
                                    style={{ display: 'inline-block' }}
                                >
                                    {letter.char}
                                </motion.span>
                            ))}
                        </motion.div>
                    </NavLink>
                </div>

                <nav className="sidebar-nav">
                    {settings?.sidebar?.home?.visible !== false && (
                        <NavLink to="/" onClick={handleHomeClick} className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
                            <span>{settings?.sidebar?.home?.title || settings?.sidebar?.homeTitle || 'HOME'}</span>
                        </NavLink>
                    )}
                    {settings?.sidebar?.shop?.visible !== false && (
                        <NavLink to="/shop" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
                            <span>{settings?.sidebar?.shop?.title || settings?.sidebar?.shopTitle || 'SHOP'}</span>
                        </NavLink>
                    )}
                    {settings?.sidebar?.customize?.visible !== false && (
                        <NavLink to="/customize" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
                            <span>{settings?.sidebar?.customize?.title || settings?.sidebar?.customizeTitle || 'CUSTOMIZE'}</span>
                        </NavLink>
                    )}
                    {settings?.sidebar?.collections?.visible !== false && (
                        <NavLink to="/collections" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
                            <span>{settings?.sidebar?.collections?.title || settings?.sidebar?.collectionsTitle || 'COLLECTIONS'}</span>
                        </NavLink>
                    )}
                    {settings?.sidebar?.contact?.visible !== false && (
                        <NavLink to="/contact" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}>
                            <span>{settings?.sidebar?.contact?.title || settings?.sidebar?.contactTitle || 'PRIVACY'}</span>
                        </NavLink>
                    )}

                    <div
                        onClick={handleUserClick}
                        className={`nav-item admin-panel-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                    >
                        <span>ADMIN PANEL</span>
                    </div>
                </nav>

                <div className="sidebar-footer">
                    <div className="sidebar-actions">
                        <div className="action-icon" onClick={() => navigate('/shop')}>
                            <FiSearch />
                        </div>
                        <div className="action-icon" onClick={() => navigate('/cart')}>
                            <FiShoppingCart />
                            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
                        </div>
                    </div>

                    {isAdminLoggedIn && (
                        <button onClick={handleLogout} className="logout-btn">
                            Logout
                        </button>
                    )}
                </div>
            </aside>

            {showLoginModal && (
                <div className="login-modal-overlay">
                    <div className="login-modal-content">
                        <button className="close-modal-btn" onClick={() => setShowLoginModal(false)}>×</button>
                        <AdminLogin
                            onLoginSuccess={() => {
                                // The AdminLogin component handles the redirection and new tab
                                // We just need to close the modal after a bit or immediately
                                // Let's wait for the success animation in AdminLogin to finish visually
                                setTimeout(() => {
                                    setIsAdminLoggedIn(true);
                                    setShowLoginModal(false);
                                    showSuccess('Welcome Admin');
                                }, 1500);
                            }}
                            onClose={() => setShowLoginModal(false)}
                        />
                    </div>
                </div>
            )}


        </>
    );
};

export default Sidebar;
